import { useCallback, useMemo, useState } from "react";
import type { InputActionMeta } from "react-select";

import { paymentClientDisplayName } from "@/modules/dashboard/payments/utils/paymentClientDisplayName";
import { Button } from "@/shared/components/form/Button";
import { InputSelect } from "@/shared/components/form/InputSelect";
import type { AuditTranslations } from "@/shared/lang/interfaces/audit";
import useTranslations from "@/shared/lang/useTranslations";

import { useDebouncedOrganizationSearch } from "../../clients/hooks/useDebouncedOrganizationSearch";
import type { ClientListItem, OrganizationSearchItem } from "../../clients/types/clients-api.types";
import { CONFIRMATION_SOURCE_OPTIONS } from "../../confirmations/constants/confirmationSourceOptions";
import { CURRENCY_OPTIONS } from "../../confirmations/constants/currencies.config";
import { FLUJO_OPTIONS, getAccionOptions } from "../constants/auditoria.constants";
import { useDebouncedAuditClientSearch } from "../hooks/useDebouncedAuditClientSearch";
import type { AuditoriaFiltersState, EntityTypeFilter, ActionFilter } from "../types/auditoria.types";

type SelectOption = { label: string; value: string };

const FLUJO_LABEL_KEYS: Record<EntityTypeFilter, keyof AuditTranslations> = {
  NOTE: "filter_flujo_note",
  PAYMENT_CONFIRMATION: "filter_flujo_payment_confirmation",
  PAYMENT: "filter_flujo_payment",
  SHIPPING_QUOTE: "filter_flujo_shipping_quote",
  PUBLIC_ACCOUNT: "filter_flujo_public_account",
};

const ACCION_LABEL_KEYS: Record<ActionFilter, keyof AuditTranslations> = {
  UPDATE: "accion_actualizacion_datos",
  STATUS_CHANGE: "accion_cambio_estado",
  CREATE: "accion_creacion",
  DELETE: "accion_eliminacion",
};

function organizationSearchLabel(o: OrganizationSearchItem): string {
  return o.name?.trim() || o.id;
}

interface AuditoriaFiltersProps {
  filters: AuditoriaFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<AuditoriaFiltersState>>;
  onApply: () => void;
  onReset: () => void;
  isLoading?: boolean;
}

export const AuditoriaFilters = ({
  filters,
  setFilters,
  onApply,
  onReset,
  isLoading = false,
}: AuditoriaFiltersProps) => {
  const { t } = useTranslations("audit");
  const { t: tConf } = useTranslations("confirmations");

  const [clientMenuInput, setClientMenuInput] = useState("");
  const [orgMenuInput, setOrgMenuInput] = useState("");
  const [selectedClientCache, setSelectedClientCache] = useState<ClientListItem | null>(null);
  const [selectedOrgCache, setSelectedOrgCache] = useState<OrganizationSearchItem | null>(null);

  const {
    clients: clientSearchResults,
    isLoading: isClientSearchLoading,
    debouncedSearch: debouncedClientSearch,
  } = useDebouncedAuditClientSearch(true, clientMenuInput);

  const {
    organizations: orgSearchResults,
    isLoading: isOrgSearchLoading,
    debouncedSearch: debouncedOrgSearch,
  } = useDebouncedOrganizationSearch(true, orgMenuInput);

  const flujoOptions: SelectOption[] = FLUJO_OPTIONS.map((o) => ({
    value: o.value,
    label: t(FLUJO_LABEL_KEYS[o.value]),
  }));
  const visibleFlujoOptions = flujoOptions.filter((o) => o.value !== "NOTE" && o.value !== "PAYMENT");
  const accionOptions: SelectOption[] = getAccionOptions(filters.flujo).map((o) => ({
    value: o.value,
    label: t(ACCION_LABEL_KEYS[o.value]),
  }));

  const currencyOptions: SelectOption[] = useMemo(
    () => CURRENCY_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
    [],
  );

  const confirmationSourceOptions: SelectOption[] = useMemo(
    () => CONFIRMATION_SOURCE_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
    [],
  );

  const clientOptions: SelectOption[] = useMemo(() => {
    const fromApi = clientSearchResults.map((c) => ({
      value: c.id,
      label: paymentClientDisplayName(c) || c.email || c.id,
    }));
    if (filters.clientId && selectedClientCache && !fromApi.some((o) => o.value === selectedClientCache.id)) {
      return [
        {
          value: selectedClientCache.id,
          label:
            paymentClientDisplayName(selectedClientCache) ||
            selectedClientCache.email ||
            selectedClientCache.id,
        },
        ...fromApi,
      ];
    }
    return fromApi;
  }, [clientSearchResults, filters.clientId, selectedClientCache]);

  const orgOptions: SelectOption[] = useMemo(() => {
    const fromApi = orgSearchResults.map((o) => ({
      value: o.id,
      label: organizationSearchLabel(o),
    }));
    if (filters.organizationId && selectedOrgCache && !fromApi.some((o) => o.value === selectedOrgCache.id)) {
      return [
        {
          value: selectedOrgCache.id,
          label: organizationSearchLabel(selectedOrgCache),
        },
        ...fromApi,
      ];
    }
    return fromApi;
  }, [orgSearchResults, filters.organizationId, selectedOrgCache]);

  const clientValue = clientOptions.find((o) => o.value === filters.clientId) ?? null;
  const orgValue = orgOptions.find((o) => o.value === filters.organizationId) ?? null;

  const currencyValue =
    filters.paymentCurrency.trim() !== ""
      ? (currencyOptions.find((o) => o.value === filters.paymentCurrency) ?? null)
      : null;

  const confirmationSourceValue =
    filters.confirmationSource.trim() !== ""
      ? (confirmationSourceOptions.find((o) => o.value === filters.confirmationSource) ?? null)
      : null;

  const handleClientInputChange = useCallback(
    (input: string, meta: InputActionMeta) => {
      if (meta.action === "input-change") {
        setClientMenuInput(input);
        setFilters((prev) => ({
          ...prev,
          organizationId: null,
        }));
        setOrgMenuInput("");
        setSelectedOrgCache(null);
      }
    },
    [setFilters],
  );

  const handleOrgInputChange = useCallback(
    (input: string, meta: InputActionMeta) => {
      if (meta.action === "input-change") {
        setOrgMenuInput(input);
        setFilters((prev) => ({
          ...prev,
          clientId: null,
        }));
        setClientMenuInput("");
        setSelectedClientCache(null);
      }
    },
    [setFilters],
  );

  const handleClientFocus = useCallback(() => {
    setFilters((prev) => ({ ...prev, organizationId: null }));
    setOrgMenuInput("");
    setSelectedOrgCache(null);
  }, [setFilters]);

  const handleOrgFocus = useCallback(() => {
    setFilters((prev) => ({ ...prev, clientId: null }));
    setClientMenuInput("");
    setSelectedClientCache(null);
  }, [setFilters]);

  const handleClientChange = useCallback(
    (selected: SelectOption | SelectOption[] | null) => {
      const opt = Array.isArray(selected) ? selected[0] : selected;
      setClientMenuInput("");
      if (!opt) {
        setSelectedClientCache(null);
        setFilters((prev) => ({ ...prev, clientId: null }));
        return;
      }
      const row =
        clientSearchResults.find((c) => c.id === opt.value) ??
        (selectedClientCache?.id === opt.value ? selectedClientCache : null);
      if (!row) return;
      setSelectedClientCache(row);
      setFilters((prev) => ({
        ...prev,
        clientId: opt.value,
        organizationId: null,
      }));
      setOrgMenuInput("");
      setSelectedOrgCache(null);
    },
    [clientSearchResults, selectedClientCache, setFilters],
  );

  const handleOrgChange = useCallback(
    (selected: SelectOption | SelectOption[] | null) => {
      const opt = Array.isArray(selected) ? selected[0] : selected;
      setOrgMenuInput("");
      if (!opt) {
        setSelectedOrgCache(null);
        setFilters((prev) => ({ ...prev, organizationId: null }));
        return;
      }
      const row =
        orgSearchResults.find((o) => o.id === opt.value) ??
        (selectedOrgCache?.id === opt.value ? selectedOrgCache : null);
      if (!row) return;
      setSelectedOrgCache(row);
      setFilters((prev) => ({
        ...prev,
        organizationId: opt.value,
        clientId: null,
      }));
      setClientMenuInput("");
      setSelectedClientCache(null);
    },
    [orgSearchResults, selectedOrgCache, setFilters],
  );

  const showClientSearchEmpty =
    debouncedClientSearch.trim().length > 0 && !isClientSearchLoading && clientSearchResults.length === 0;

  const showOrgSearchEmpty =
    debouncedOrgSearch.trim().length > 0 && !isOrgSearchLoading && orgSearchResults.length === 0;

  const handleReset = useCallback(() => {
    setClientMenuInput("");
    setOrgMenuInput("");
    setSelectedClientCache(null);
    setSelectedOrgCache(null);
    onReset();
  }, [onReset]);

  return (
    <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("filters_heading")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_flujo")}
          </label>
          <InputSelect<SelectOption>
            options={visibleFlujoOptions}
            value={filters.flujo ? (flujoOptions.find((o) => o.value === filters.flujo) ?? null) : null}
            onChange={(selected) => {
              const v = Array.isArray(selected) ? selected[0]?.value : selected?.value;
              const newFlujo = (v ?? "") as EntityTypeFilter | "";
              setFilters((prev) => {
                const next = { ...prev, flujo: newFlujo };
                if (newFlujo === "NOTE" && prev.accion && !["CREATE", "DELETE"].includes(prev.accion)) {
                  next.accion = "";
                }
                return next;
              });
            }}
            placeholder={t("filters_placeholder_flujo")}
            maxWidth="w-full"
            controlClassName="!h-11"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_accion")}
          </label>
          <InputSelect<SelectOption>
            options={accionOptions}
            value={filters.accion ? (accionOptions.find((o) => o.value === filters.accion) ?? null) : null}
            onChange={(selected) => {
              const v = Array.isArray(selected) ? selected[0]?.value : selected?.value;
              setFilters((prev) => ({ ...prev, accion: (v ?? "") as ActionFilter | "" }));
            }}
            placeholder={t("filters_placeholder_accion")}
            maxWidth="w-full"
            controlClassName="!h-11"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_date_start")}
          </label>
          <input
            type="date"
            value={filters.fechaInicio}
            onChange={(e) => setFilters((prev) => ({ ...prev, fechaInicio: e.target.value }))}
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_date_end")}
          </label>
          <input
            type="date"
            value={filters.fechaFinal}
            onChange={(e) => setFilters((prev) => ({ ...prev, fechaFinal: e.target.value }))}
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        <div className="hidden space-y-2 min-w-0">
          <InputSelect<SelectOption>
            name="audit-filters-organization"
            label={t("filters_label_organization")}
            options={orgOptions}
            value={orgValue}
            onChange={(v) => handleOrgChange((Array.isArray(v) ? v[0] : v) ?? null)}
            onInputChange={handleOrgInputChange}
            onFocus={handleOrgFocus}
            filterOption={() => true}
            placeholder={t("filters_placeholder_organization")}
            isLoading={isOrgSearchLoading}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
            noOptionsMessage={() => t("filters_dropdown_empty")}
          />
          {showOrgSearchEmpty && (
            <p className="text-sm text-amber-700 dark:text-amber-300/90" role="status">
              {t("filters_dropdown_empty")}
            </p>
          )}
        </div>
        <div className="hidden space-y-2 min-w-0">
          <InputSelect<SelectOption>
            name="audit-filters-client"
            label={t("filters_label_client")}
            options={clientOptions}
            value={clientValue}
            onChange={(v) => handleClientChange((Array.isArray(v) ? v[0] : v) ?? null)}
            onInputChange={handleClientInputChange}
            onFocus={handleClientFocus}
            filterOption={() => true}
            placeholder={t("filters_placeholder_client")}
            isLoading={isClientSearchLoading}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
            noOptionsMessage={() => t("filters_dropdown_empty")}
          />
          {showClientSearchEmpty && (
            <p className="text-sm text-amber-700 dark:text-amber-300/90" role="status">
              {t("filters_dropdown_empty")}
            </p>
          )}
        </div>
        <div className="hidden">
          <InputSelect<SelectOption>
            name="audit-filters-currency"
            label={tConf("form_label_currency")}
            options={currencyOptions}
            value={currencyValue}
            onChange={(selected) => {
              const v = Array.isArray(selected) ? selected[0]?.value : selected?.value;
              setFilters((prev) => ({
                ...prev,
                paymentCurrency: typeof v === "string" ? v : "",
              }));
            }}
            placeholder={tConf("form_placeholder_currency")}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
          />
        </div>
      </div>

      <div className="hidden grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_amount_min")}
          </label>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={filters.paymentAmountMin}
            onChange={(e) => setFilters((prev) => ({ ...prev, paymentAmountMin: e.target.value }))}
            placeholder={t("filters_placeholder_amount_min")}
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_amount_max")}
          </label>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={filters.paymentAmountMax}
            onChange={(e) => setFilters((prev) => ({ ...prev, paymentAmountMax: e.target.value }))}
            placeholder={t("filters_placeholder_amount_max")}
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div className="md:col-span-1">
          <InputSelect<SelectOption>
            name="audit-filters-confirmation-source"
            label={t("filters_label_confirmation_source")}
            options={confirmationSourceOptions}
            value={confirmationSourceValue}
            onChange={(selected) => {
              const v = Array.isArray(selected) ? selected[0]?.value : selected?.value;
              setFilters((prev) => ({
                ...prev,
                confirmationSource: typeof v === "string" ? v : "",
              }));
            }}
            placeholder={t("filters_placeholder_confirmation_source")}
            isClearable
            maxWidth="max-w-full"
            controlClassName="!h-11"
          />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t("filters_hint")}</p>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("filters_label_search")}
          </label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            placeholder={t("filters_placeholder_search")}
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div className="flex gap-3 mt-4">
        <Button type="button" variant="primary" onClick={onApply} disabled={isLoading}>
          {t("filters_button_apply")}
        </Button>
        <Button type="button" variant="outline" onClick={handleReset} disabled={isLoading}>
          {t("filters_button_clear")}
        </Button>
      </div>
    </div>
  );
};
